import React from 'react';
import { ImportSessionStorage } from 'utils/importSessionStorage';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router';
import MagicButton from 'components/MagicButton/MagicButton';
import { AssetUrls } from 'config/assets';
import './LoginPromptModal.css';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  collectionOwnerUsername: string;
  /**
   * Fired only when the prompt is dismissed (overlay or close button), not when
   * Sign Up / Log In send the user to /welcome. Callers that stash a post-auth
   * destination before opening this use it to drop that value when the user
   * walks away, since onClose fires for both cases.
   */
  onDismiss?: () => void;
  /**
   * Sign Up / Log In clear any stored post-auth destination before leaving
   * (Asana 1220218671326301, Decision 3), so a stale Play return address can't
   * redirect a deliberate trading sign-in. A caller that stashed its own
   * destination right before opening this (ViewCardPage) passes true.
   */
  preserveReturnTo?: boolean;
}

const LoginPromptModal: React.FC<Props> = ({ isOpen, onClose, collectionOwnerUsername, onDismiss, preserveReturnTo = false }) => {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleDismiss = () => {
    onDismiss?.();
    onClose();
  };

  const goToWelcome = () => {
    if (!preserveReturnTo) ImportSessionStorage.clearReturnTo();
    navigate('/welcome');
    onClose();
  };

  const handleSignUp = goToWelcome;
  const handleLogIn = goToWelcome;

  const modalContent = (
    <div className="login-prompt-overlay" onClick={handleDismiss}>
      <div className="login-prompt-modal" onClick={(e) => e.stopPropagation()}>
        <button className="login-prompt-close-button" onClick={handleDismiss}>
          <img
            src={AssetUrls.icons.dropdowns.close}
            alt="Close"
            style={{ width: 16, height: 16 }}
          />
        </button>

        <div className="login-prompt-content">
          <div className="login-prompt-icon">🎴</div>

          <h2 className="login-prompt-title">Join TradeMagic to Start Trading</h2>

          <p className="login-prompt-body">
            Create an account to trade cards with <strong>{collectionOwnerUsername}</strong> and thousands of other players.
          </p>

          <div className="login-prompt-actions">
            <MagicButton
              label="Sign Up for Free"
              color="purple"
              onPress={handleSignUp}
              fullWidth
            />

            <div className="login-prompt-secondary">
              Already have an account?{' '}
              <button className="login-prompt-link" onClick={handleLogIn}>
                Log In
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default LoginPromptModal;
